import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight } from "@/lib/icons";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { MarGrid } from "@/components/app/mar/mar-grid";
import { orderChipFlags } from "@/components/app/orders/order-utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { MedicationOrderCard } from "./-components/documents/medication-order-card";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/mar")({
  component: MedicationsTab,
});

const ENDED = new Set(["discontinued", "completed"]);

/** The resident's medications and the monthly MAR. Orders are written in the Orders tab; this is
 * where a dose is recorded and its history read. */
function MedicationsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canAdminister = useCan("medication:administer", resident.facilityId);
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const orders = useQuery(
    orpc.care.residents.orders.list.queryOptions({
      input: { residentId, includeDiscontinued: true },
    }),
  );

  const medications = (orders.data ?? []).filter((order) => order.orderType === "medication");
  const active = medications.filter((order) => !ENDED.has(order.status));
  const ended = medications.filter((order) => ENDED.has(order.status));
  const prnCount = active.filter((order) => order.scheduleMode === "prn").length;
  const controlledCount = active.filter((order) => order.isControlled).length;
  const needsReviewCount = active.filter(
    (order) => orderChipFlags(order).unreviewed || order.needsScheduleReview,
  ).length;

  const manageInOrders = (
    <Button variant="outline" asChild>
      <Link to="/residents/$residentId/medications" params={{ residentId }}>
        Manage in Orders
      </Link>
    </Button>
  );
  const card = (order: (typeof medications)[number]) => (
    <MedicationOrderCard
      key={order.id}
      order={order}
      residentId={residentId}
      canAdminister={canAdminister}
    />
  );

  return (
    <div className="space-y-6">
      <Tabs defaultValue="medications">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Medications</h2>
            <p className="text-muted-foreground text-sm">
              Current medication orders and the monthly MAR
            </p>
          </div>
          <div className="flex items-center gap-2">
            <TabsList>
              <TabsTrigger value="medications">Medications</TabsTrigger>
              <TabsTrigger value="mar">MAR</TabsTrigger>
            </TabsList>
            {manageInOrders}
          </div>
        </div>

        <TabsContent value="medications" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{active.length} active</Badge>
            <Badge variant={prnCount > 0 ? "prn" : "outline"}>{prnCount} PRN</Badge>
            <Badge variant={controlledCount > 0 ? "controlled" : "outline"}>
              {controlledCount} controlled
            </Badge>
            {needsReviewCount > 0 ? (
              <Badge variant="warn">
                <AlertTriangle className="size-3" />
                {needsReviewCount} needs review
              </Badge>
            ) : null}
          </div>

          {orders.isPending ? (
            <div className="space-y-4">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          ) : orders.isError ? (
            /* A failed read is never "no medications": staff act on that. */
            <ReadFailed
              message="Could not load this resident's medication orders."
              error={orders.error}
              onRetry={() => void orders.refetch()}
              retrying={orders.isFetching}
            />
          ) : medications.length === 0 ? (
            <Card className="py-0">
              <EmptyState
                title="No medication orders yet"
                description="Medication orders are created in the Orders tab. Add the first order there and it will appear here and on the MAR."
                action={
                  <Button asChild>
                    <Link to="/residents/$residentId/medications" params={{ residentId }}>
                      Go to Orders
                    </Link>
                  </Button>
                }
              />
            </Card>
          ) : (
            <>
              <div className="space-y-4">
                {active.map(card)}
                {active.length === 0 ? (
                  <Card className="py-0">
                    <EmptyState
                      title="No active medications"
                      description="Discontinued orders are listed below."
                    />
                  </Card>
                ) : null}
              </div>
              {ended.length > 0 ? (
                <div className="space-y-4">
                  <Button
                    variant="ghost"
                    className="text-muted-foreground"
                    aria-expanded={showDiscontinued}
                    onClick={() => setShowDiscontinued((shown) => !shown)}
                  >
                    {showDiscontinued ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                    {showDiscontinued ? "Hide" : "Show"} discontinued ({ended.length})
                  </Button>
                  {showDiscontinued ? <div className="space-y-4">{ended.map(card)}</div> : null}
                </div>
              ) : null}
            </>
          )}
        </TabsContent>

        <TabsContent value="mar" className="mt-4">
          <MarGrid residentId={residentId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
